import { useEffect, useRef } from 'react';
import { PostHogProvider as PHProvider } from '@posthog/react';

import { startConsentUpdatedTracking } from '@/lib/consentUpdated';
import {
  hasCookieYesConsentDecision,
  isAnalyticsConsentGranted,
  parseCookieYesConsent,
  resolveAnalyticsFromCookieYesEvent,
} from '@/lib/cookieyes';
import {
  initPostHog,
  posthog,
  revokePostHogCapturing,
} from '@/lib/posthog';

export const PostHogProvider = ({ children }: { children: React.ReactNode }) => {
  const hadDecisionRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!import.meta.env.VITE_POSTHOG_PROJECT_TOKEN) return;

    const applySdkConsent = (event?: Event) => {
      const consent = parseCookieYesConsent();
      const analyticsFromEvent = resolveAnalyticsFromCookieYesEvent(event);
      const analyticsAllowed =
        analyticsFromEvent ?? isAnalyticsConsentGranted(consent);
      const hasDecision =
        analyticsFromEvent !== undefined || hasCookieYesConsentDecision(consent);

      if (!hasDecision) return;

      hadDecisionRef.current = true;

      if (analyticsAllowed) {
        initPostHog();
        return;
      }

      revokePostHogCapturing();
    };

    hadDecisionRef.current = hasCookieYesConsentDecision();
    applySdkConsent();

    const stopConsentTracking = startConsentUpdatedTracking({
      hadPreviousDecision: () => hadDecisionRef.current,
      onTracked: applySdkConsent,
    });

    document.addEventListener('cookieyes_banner_load', applySdkConsent);

    return () => {
      stopConsentTracking();
      document.removeEventListener('cookieyes_banner_load', applySdkConsent);
    };
  }, []);

  return <PHProvider client={posthog}>{children}</PHProvider>;
};
